/**
 * ReportFilterBar + ReportShell -- shared skeleton used by every HQ
 * Sales Report page. Keeps the filter UX consistent (date range preset,
 * store filter, CSV export) and lets each report plug in just its own
 * title, column spec and row renderer.
 */
import { useEffect, useMemo, useState } from 'react';
import { Download, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import api from '@/services/api';
import { todayIST, shiftDateStr } from '@/utils/dateFormat';

const INR = (n) => (Number(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const RANGE_PRESETS = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'week', label: 'This Week' },
  { key: 'month', label: 'This Month' },
  { key: 'last30', label: 'Last 30 Days' },
  { key: 'last_month', label: 'Last Month' },
  { key: 'custom', label: 'Custom' },
];

export function computeRange(preset) {
  const today = todayIST();
  if (preset === 'today') {
    return { date_from: today, date_to: today };
  }
  if (preset === 'yesterday') {
    const y = shiftDateStr(today, -1);
    return { date_from: y, date_to: y };
  }
  if (preset === 'week') {
    const dow = new Date(`${today}T00:00:00Z`).getUTCDay(); // 0 = sunday
    return { date_from: shiftDateStr(today, -dow), date_to: today };
  }
  if (preset === 'last30') {
    return { date_from: shiftDateStr(today, -30), date_to: today };
  }
  if (preset === 'last_month') {
    const firstThis = `${today.slice(0, 8)}01`;
    const lastPrev = shiftDateStr(firstThis, -1);
    return { date_from: `${lastPrev.slice(0, 8)}01`, date_to: lastPrev };
  }
  // month default
  return { date_from: `${today.slice(0, 8)}01`, date_to: today };
}

export function csvDownload(rows, filename) {
  if (!rows || rows.length === 0) return;
  const keys = Object.keys(rows[0]);
  const esc = (v) => {
    if (v == null) return '';
    const s = String(v).replace(/"/g, '""');
    return /[",\n]/.test(s) ? `"${s}"` : s;
  };
  const body = [keys.join(','), ...rows.map(r => keys.map(k => esc(r[k])).join(','))].join('\n');
  const blob = new Blob([body], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function ReportFilterBar({
  preset, onPresetChange, dateFrom, onDateFrom, dateTo, onDateTo,
  storeId, onStoreChange, stores, extra, onRefresh, onExport,
}) {
  return (
    <div className="flex flex-wrap items-end gap-2 pb-4 border-b border-gray-200 mb-4">
      <div className="min-w-[9rem]">
        <label className="block text-xs text-gray-500 mb-1">Range</label>
        <Select value={preset} onValueChange={onPresetChange}>
          <SelectTrigger data-testid="report-range-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {RANGE_PRESETS.map(r => (<SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="block text-xs text-gray-500 mb-1">From</label>
        <Input type="date" value={dateFrom} onChange={(e) => { onDateFrom(e.target.value); onPresetChange('custom'); }} className="w-36" />
      </div>
      <div>
        <label className="block text-xs text-gray-500 mb-1">To</label>
        <Input type="date" value={dateTo} onChange={(e) => { onDateTo(e.target.value); onPresetChange('custom'); }} className="w-36" />
      </div>
      {onStoreChange && (
        <div className="min-w-[12rem]">
          <label className="block text-xs text-gray-500 mb-1">Store</label>
          <Select value={storeId || 'all'} onValueChange={onStoreChange}>
            <SelectTrigger data-testid="report-store-select"><SelectValue placeholder="All stores" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All stores</SelectItem>
              {(stores || []).map(s => (<SelectItem key={s.id} value={s.id}>{s.code} — {s.name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
      )}
      {extra}
      <div className="ml-auto flex gap-2">
        <Button variant="outline" size="sm" onClick={onRefresh}>
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
        <Button variant="outline" size="sm" onClick={onExport} data-testid="report-csv-btn">
          <Download className="w-4 h-4 mr-1" /> CSV
        </Button>
      </div>
    </div>
  );
}

/**
 * ReportShell -- fetches a given endpoint on filter change, renders
 * the table using the supplied columns.
 *
 *   columns: [{ key, label, className?, format?, align? }]
 *   fetchParams: () => object  (query params for the endpoint)
 *   url: '/reports/sales/<name>'
 *   responseKey: 'rows' | 'bills'
 *   title, csvName, children (for extra actions above table)
 */
export function ReportShell({
  title, url, responseKey, columns, csvName, buildParams, trigger,
  showStoreFilter = true, extra, defaultPreset = 'month', children,
  onRowClick, totalsRow, deps = [], renderSummary, rowClassName,
}) {
  const { toast } = useToast();
  const [preset, setPreset] = useState(defaultPreset);
  const initRange = computeRange(defaultPreset);
  const [dateFrom, setDateFrom] = useState(initRange.date_from);
  const [dateTo, setDateTo] = useState(initRange.date_to);
  const [storeId, setStoreId] = useState('all');
  const [stores, setStores] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!showStoreFilter) return;
    (async () => {
      try {
        const res = await api.get('/stores');
        setStores(res.data?.stores || res.data || []);
      } catch {}
    })();
  }, [showStoreFilter]);

  useEffect(() => {
    if (preset !== 'custom') {
      const r = computeRange(preset);
      setDateFrom(r.date_from);
      setDateTo(r.date_to);
    }
  }, [preset]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { date_from: dateFrom, date_to: dateTo };
      if (showStoreFilter && storeId && storeId !== 'all') params.store_id = storeId;
      if (buildParams) Object.assign(params, buildParams());
      const res = await api.get(url, { params });
      setData(res.data);
    } catch (err) {
      toast({ title: 'Load failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-line */ }, [dateFrom, dateTo, storeId, trigger, ...deps]);

  const rows = data?.[responseKey] || [];

  const flatForCsv = useMemo(() => rows.map(r => {
    const out = {};
    columns.forEach(c => {
      let v = typeof c.key === 'function' ? c.key(r) : r[c.key];
      if (c.csvFormat) v = c.csvFormat(v, r);
      else if (c.format && typeof v !== 'object') v = c.format(v, r);
      out[c.label] = v ?? '';
    });
    return out;
  }), [rows, columns]);

  return (
    <div className="p-6 lg:p-8 bg-gray-50 min-h-screen">
      <Card>
        <CardHeader>
          <CardTitle className="text-xl">{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <ReportFilterBar
            preset={preset} onPresetChange={setPreset}
            dateFrom={dateFrom} onDateFrom={setDateFrom}
            dateTo={dateTo} onDateTo={setDateTo}
            storeId={showStoreFilter ? storeId : undefined}
            onStoreChange={showStoreFilter ? setStoreId : undefined}
            stores={stores} extra={extra}
            onRefresh={load}
            onExport={() => csvDownload(flatForCsv, `${csvName}-${dateFrom}-to-${dateTo}.csv`)}
          />
          {children}
          {!loading && data && renderSummary && renderSummary(data)}
          {loading ? (
            <div className="py-12 text-center text-gray-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading…</div>
          ) : rows.length === 0 ? (
            <div className="py-12 text-center text-gray-400">No records in this range.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  {columns.map(c => (
                    <TableHead key={c.label} className={`${c.align === 'right' ? 'text-right' : ''} ${c.headClassName || ''}`}>{c.label}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r, idx) => (
                  <TableRow key={r.id || r._id || r.product_id || idx} className={`${onRowClick ? 'cursor-pointer hover:bg-gray-50' : ''} ${rowClassName ? rowClassName(r) : ''}`} onClick={() => onRowClick?.(r)}>
                    {columns.map(c => {
                      let v = typeof c.key === 'function' ? c.key(r) : r[c.key];
                      if (c.format) v = c.format(v, r, idx);
                      return <TableCell key={c.label} className={`${c.align === 'right' ? 'text-right' : ''} ${c.className || ''}`}>{v}</TableCell>;
                    })}
                  </TableRow>
                ))}
                {totalsRow && (
                  <TableRow className="bg-amber-50 font-bold">
                    {totalsRow(data).map((v, i) => (
                      <TableCell key={i} className={columns[i]?.align === 'right' ? 'text-right' : ''}>{v}</TableCell>
                    ))}
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export const money = (v) => `₹ ${INR(v)}`;
export const num = (v) => (Number(v) || 0).toLocaleString('en-IN');
export const dateFmt = (v) => {
  if (!v) return '';
  try { return new Date(v).toLocaleDateString('en-IN'); } catch { return v; }
};
